import styled, { keyframes } from 'styled-components';
import { Box, theme } from '@bpba/ui-components';
import { TableStyles } from 'react-data-table-component';

export const PAGE_SIZE = 5;

export const monthNames: Record<string, string> = {
  '01': 'Enero',
  '02': 'Febrero',
  '03': 'Marzo',
  '04': 'Abril',
  '05': 'Mayo',
  '06': 'Junio',
  '07': 'Julio',
  '08': 'Agosto',
  '09': 'Septiembre',
  '10': 'Octubre',
  '11': 'Noviembre',
  '12': 'Diciembre',
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const FadeSwap = styled.div`
  animation: ${fadeIn} 0.4s ease-out both;
`;

export const ResponsiveGapBox = styled(Box)`
  gap: 16px;
`;

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.space[5]}px;
  min-height: 370px;
  text-align: center;
`;

export const PageBtn = styled.button<{ $active: boolean }>(({ $active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '32px',
  height: '32px',
  padding: '4px',
  border: 'none',
  borderRadius: '4px',
  fontSize: '13px',
  fontWeight: $active ? 500 : 400,
  cursor: $active ? 'default' : 'pointer',
  backgroundColor: $active ? theme.colors.primary[0] : theme.colors.secondary[2],
  color: $active ? theme.colors.white : theme.colors.secondary[1],
  ...(!$active && {
    '&:hover': { backgroundColor: theme.colors.secondary[0] },
  }),
}));

export const Ellipsis = styled.span({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '32px',
  height: '32px',
  fontSize: '13px',
  color: theme.colors.secondary[1],
  userSelect: 'none',
});

export const ArrowBtn = styled.button<{ $disabled: boolean }>(({ $disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  flexShrink: 0,
  border: 'none',
  borderRadius: '4px',
  backgroundColor: theme.colors.secondary[2],
  color: $disabled ? theme.colors.skeleton.secondary : theme.colors.secondary[1],
  cursor: $disabled ? 'default' : 'pointer',
  pointerEvents: $disabled ? ('none' as const) : ('auto' as const),
  ...(!$disabled && {
    '&:hover': { backgroundColor: theme.colors.secondary[0] },
  }),
}));

export const customStyles: TableStyles = {
  table: {
    style: { width: '100%', backgroundColor: 'transparent' },
  },
  headRow: {
    style: {
      backgroundColor: theme.colors.primary[0],
      color: theme.colors.white,
      fontSize: '14px',
      fontWeight: 500,
      minHeight: '50px',
      borderBottomColor: theme.colors.primary[0],
    },
  },
  headCells: {
    style: {
      paddingLeft: '12px',
      paddingRight: '12px',
      backgroundColor: 'transparent',
      fontWeight: 500,
      '&:first-of-type': { paddingLeft: '24px' },
      '&:last-of-type': { paddingRight: '24px' },
      '@media (max-width: 768px)': {
        paddingLeft: '8px',
        paddingRight: '8px',
        '&:first-of-type': { paddingLeft: '16px' },
        '&:last-of-type': { paddingRight: '16px' },
      },
    },
  },
  rows: {
    style: {
      backgroundColor: 'transparent',
      minHeight: '58px',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
      // fijamos el color del divider en no-hover para poder repetirlo en hover
      // y así el borde inferior no cambia al pasar el mouse.
      '&:not(:last-of-type)': {
        borderBottomColor: 'rgba(0,0,0,0.12)',
      },
    },
    highlightOnHoverStyle: {
      backgroundColor: 'transparent',
      cursor: 'pointer',
      color: theme.colors.primary[0],
      // pisamos los defaults del lib para NO tocar el borde inferior en hover
      borderBottomColor: 'rgba(0,0,0,0.12)',
      outlineStyle: 'none',
      outlineWidth: 0,
      outlineColor: 'transparent',
    },
  },
  cells: {
    style: {
      paddingTop: '10px',
      paddingBottom: '10px',
      paddingLeft: '10px',
      paddingRight: '10px',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      backgroundColor: 'transparent',
      fontWeight: 500,
      '&:first-of-type': { paddingLeft: '24px' },
      '&:last-of-type': { paddingRight: '24px' },
      '@media (max-width: 768px)': {
        paddingLeft: '8px',
        paddingRight: '8px',
        '&:first-of-type': { paddingLeft: '16px' },
        '&:last-of-type': { paddingRight: '16px' },
      },
    },
  },
};

export type IdLabel = { id: string; label: string };
export type FiltersForm = { informe: string; year: string; month: string };
export const DEFAULT_FILTERS: FiltersForm = { informe: '', year: '', month: '' };
